import { deepEqual } from './deepEqual';
import { isObject } from './isObject';

type ConfigObject = Record<string, unknown>;

/**
 * Extracts only the changed fields between original and updated config.
 * Returns an object with the structure of changed paths and their new values.
 *
 * Note: Deletion of object keys is not supported by the backend API.
 * Only array element changes are supported (arrays are replaced entirely).
 *
 * Example:
 *   original: { a: 1, b: { c: 2, d: 3 } }
 *   updated:  { a: 1, b: { c: 5, d: 3 } }
 *   result:   { b: { c: 5 } }
 */
export function getChangedFields(original: ConfigObject, updated: ConfigObject): ConfigObject {
  const changes: ConfigObject = {};

  // Check all keys in updated object
  Object.keys(updated).forEach((key) => {
    const originalValue = original[key];
    const updatedValue = updated[key];

    // If values are equal, skip
    if (deepEqual(originalValue, updatedValue)) {
      return;
    }

    // If both are objects (but not arrays), recurse to get nested changes
    if (isObject(originalValue) && isObject(updatedValue) && !Array.isArray(originalValue) && !Array.isArray(updatedValue)) {
      const nestedChanges = getChangedFields(originalValue as ConfigObject, updatedValue as ConfigObject);
      if (Object.keys(nestedChanges).length > 0) {
        changes[key] = nestedChanges;
      }
    } else {
      // For primitives, arrays, or type changes - include the full new value
      changes[key] = updatedValue;
    }
  });

  // Note: Removed keys are ignored - backend does not support key deletion
  // Arrays are replaced entirely, so removing array elements is supported

  return changes;
}

/**
 * Extracts the previous values for only the changed fields.
 * Returns an object with the same structure as changedFields but with original values.
 *
 * Example:
 *   original: { a: 1, b: { c: 2, d: 3 } }
 *   changedFields: { b: { c: 5 } }
 *   result:   { b: { c: 2 } }
 */
export function getPreviousValues(original: ConfigObject, changedFields: ConfigObject): ConfigObject {
  const previousValues: ConfigObject = {};

  Object.keys(changedFields).forEach((key) => {
    const changedValue = changedFields[key];
    const originalValue = original[key];

    // If the changed value is an object (nested changes), recurse
    if (isObject(changedValue) && isObject(originalValue) && !Array.isArray(changedValue) && !Array.isArray(originalValue)) {
      const nestedPrevious = getPreviousValues(originalValue as ConfigObject, changedValue as ConfigObject);
      if (Object.keys(nestedPrevious).length > 0) {
        previousValues[key] = nestedPrevious;
      }
    } else {
      // For primitives or arrays - include the original value
      previousValues[key] = originalValue;
    }
  });

  return previousValues;
}
